import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { API_URLS } from '../constants/apiUrl';
import { useLoading } from '../context/LoadingContext';
import { post } from '../api/api';
import { showToast } from '../components/utils/toast';
import { AnalyticsFeatureOperation } from '../interface/analytics';
import { useAnalytics } from './useAnalytics';
import { IActionDto } from '../interface/common';
import { VERSION_CONFLICT_CONFIG } from '../helpers/versionConflictHelper';
import { TASK_QUERY_KEYS_TO_INVALIDATE } from '../constants/tasks';
import { useShowVersionConflict } from './useVersionConflict';

const markActivityAsCompleted = async ({ id, version }: IActionDto): Promise<string> => {
  return post(
    API_URLS.MARK_ACTIVITY_AS_COMPLETED.replace(':id', id),
    { version },
    VERSION_CONFLICT_CONFIG,
  );
};

export const useMarkActivityAsCompleted = (): UseMutationResult<
  string,
  AxiosError<{ message?: string; type?: string }>,
  IActionDto
> => {
  const { setIsLoading } = useLoading();
  const { t } = useTranslation();
  const { trackBusinessEvent } = useAnalytics();
  const showVersionConflict = useShowVersionConflict();

  return useMutation({
    mutationFn: markActivityAsCompleted,
    onMutate: () => {
      setIsLoading(true);
    },
    onSuccess: () => {
      trackBusinessEvent(AnalyticsFeatureOperation.TASK_COMPLETED);
      showToast('success', t('task:markedAsComplete'));
    },
    onError: (error) => {
      if (showVersionConflict(error, TASK_QUERY_KEYS_TO_INVALIDATE)) return;
      showToast('error', error.message);
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });
};
